import { cloneElement, type ReactElement, useRef } from 'react';
import { mergeProps, useHover } from 'react-aria';

import { Tooltip, type Props as TooltipProps } from './Tooltip';

export type Props = Pick<TooltipProps, 'text' | 'theme' | 'placement'> & {
  /**
   * The element the pointer hovers, which is also what the bubble is positioned against. Cloned, so
   * it must be a single element that accepts a ref and DOM props: `<span>`, `<div>`, a styled
   * component, not a fragment and not a text node.
   */
  children: ReactElement;
  /**
   * Required, and required to be `true`, so the type system makes the call site state that the same
   * text is reachable some other way — normally `<VisuallyHidden>{text}</VisuallyHidden>` beside
   * this. Nothing reads it at runtime. It exists because the alternative is a component whose one
   * accessibility obligation is discoverable only by reading this file.
   */
  textAlsoRenderedForScreenReaders: true;
};

/**
 * A tooltip that reveals on pointer hover only, for a target that must not be focusable.
 *
 * Use `Tooltip` unless you have this exact problem: the tooltip's target is inside a control that is
 * deliberately out of the tab order, so the target cannot be made focusable to carry the tooltip.
 * `Tooltip`'s escape hatch for a non-interactive target, `isNonFocusableTarget`, wraps it in
 * `Focusable` — that is, it fixes the problem by making the target focusable, which is the opposite
 * of what a disabled control needs. The shipped cases are both the reason on a disabled tab —
 * `tab-switches/button-menu`, and `navigation/underline-nav`'s coming-soon tab (PLT-6309): a tab
 * stop inside a disabled tab is a defect, so its reason cannot be attached by adding one.
 *
 * **What this costs, and it is not small.** The reveal is pointer-only: keyboard users never see it,
 * screen readers are never told about it, and react-aria's `useHover` ignores touch pointers by
 * design, so a tablet gets nothing either. Not told about it is literal rather than hopeful: the
 * bubble still renders with react-aria's `role="tooltip"`, but a tooltip is announced because
 * something references it, and the `aria-describedby` that would do that is wired up by
 * `FocusableProvider` on a focusable trigger — which this deliberately does not have. So the bubble
 * is unreferenced rather than hidden, and the out-of-band copy is the only thing read out. That makes it a decoration for mouse users rather than an
 * accessible affordance, and the text must therefore also be rendered out of band — visually hidden
 * text beside it, or the control's own accessible name. `textAlsoRenderedForScreenReaders` is how the
 * call site says it did that. Accepted for the one case above rather than as a pattern: PLT-6171
 * carries the decision and the rule is written down (DESIGN.md, "Accessibility").
 *
 * **How it works.** All the chrome, the bubble, the arrow, the animation and the theme come from
 * `Tooltip`; this only replaces what opens it. `Tooltip` already accepts `isOpen` and `triggerRef`,
 * so the two things react-aria needs are supplied from here: hover state from `useHover` on the
 * cloned child, and the child's own DOM node as the positioning anchor. The child has to be that
 * anchor — react-aria measures it with `getBoundingClientRect`, and a `display: contents` wrapper of
 * the kind `Tooltip`'s `openOnPress` uses to catch clicks measures as zeros, so it cannot position
 * anything. react-aria's own hover and focus handlers on the trigger are inert here: they are
 * delivered through `FocusableProvider`, and a plain cloned element does not consume that context,
 * which is exactly why focus does not open this and no handler runs twice. `Tooltip`'s `delay` and
 * `closeDelay` are inert for the same reason, so the reveal is immediate — matching the Primer
 * tooltip this replaced at that call site, which also opened straight out of `onMouseEnter`.
 */
export function PointerOnlyTooltip({ children, text, theme, placement }: Props) {
  const triggerRef = useRef<HTMLElement>(null);
  // isHovered comes from useHover rather than being mirrored into state of our own: the hook already
  // holds it, and a second copy would only add a render and a way for the two to disagree.
  const { hoverProps, isHovered } = useHover({});

  return (
    <Tooltip text={text} theme={theme} placement={placement} isOpen={isHovered} triggerRef={triggerRef}>
      {/* The child's props win over the hover handlers, and `mergeProps` chains rather than replaces
          them, so a child with its own onPointerEnter keeps it. Its own `ref` does not survive,
          which is the same limitation `Box`'s `asChild` has. The cast is because React types a
          generic `ReactElement`'s props as `any`, and handing that to `mergeProps` unchecked is
          what `no-unsafe-argument` flags — the shape genuinely is unknown props here. */}
      {cloneElement(children, mergeProps(hoverProps, children.props as Record<string, unknown>, { ref: triggerRef }))}
    </Tooltip>
  );
}
